import React, { useMemo } from "react";
import { Trash2 } from "lucide-react";

// Largest font (px) whose word-wrapped text fits inside a w×h box. Mirrors the
// backend's fit-to-box logic so the on-screen overlay matches the output PDF.
let _measureCtx = null;
const fitFontPx = (text, maxW, maxH, family = "Outfit, sans-serif") => {
  if (!text || maxW <= 0 || maxH <= 0) return 12;
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  const ctx = _measureCtx;
  const words = String(text).split(/\s+/).filter(Boolean);
  const fits = (size) => {
    ctx.font = `${size}px ${family}`;
    const lineH = size * 1.3;
    let lines = 1;
    let line = "";
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width <= maxW) {
        line = test;
      } else {
        lines += 1;
        line = w;
      }
    }
    return lines * lineH <= maxH;
  };
  let lo = 6;
  let hi = 48;
  let best = 6;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
};

const HANDLE_SIZE = 8;
const HALF_HANDLE = HANDLE_SIZE / 2;

const RESIZE_HANDLES = [
  { kind: "nw", style: { left: -HALF_HANDLE, top: -HALF_HANDLE, cursor: "nwse-resize" } },
  { kind: "n", style: { left: "50%", top: -HALF_HANDLE, marginLeft: -HALF_HANDLE, cursor: "ns-resize" } },
  { kind: "ne", style: { right: -HALF_HANDLE, top: -HALF_HANDLE, cursor: "nesw-resize" } },
  { kind: "e", style: { right: -HALF_HANDLE, top: "50%", marginTop: -HALF_HANDLE, cursor: "ew-resize" } },
  { kind: "se", style: { right: -HALF_HANDLE, bottom: -HALF_HANDLE, cursor: "nwse-resize" } },
  { kind: "s", style: { left: "50%", bottom: -HALF_HANDLE, marginLeft: -HALF_HANDLE, cursor: "ns-resize" } },
  { kind: "sw", style: { left: -HALF_HANDLE, bottom: -HALF_HANDLE, cursor: "nesw-resize" } },
  { kind: "w", style: { left: -HALF_HANDLE, top: "50%", marginTop: -HALF_HANDLE, cursor: "ew-resize" } },
];

/**
 * Renders a single field's bbox overlay on the PDF page — including the
 * answer text, hover-only delete button, and 8 resize handles. Move/resize
 * gestures are started by the parent's `onBboxMouseDown` handler.
 */
const BboxOverlay = ({
  field,
  css,
  showAnswer,
  isOverridden,
  isViewMode,
  isFinalizedView,
  drawMode,
  showAffordances,
  isHighlighted,
  scale,
  hasAnswers,
  onMouseEnter,
  onMouseLeave,
  onBboxMouseDown,
  onRequestDelete,
  onActivate,
}) => {
  const answer = field.answer || "";
  const isCheck = field.field_type === "checkbox";
  const isSignature =
    field.field_type === "signature" && /^data:image\//.test(answer);
  const checked =
    isCheck &&
    ["true", "1", "yes", "checked", "on"].includes(
      String(answer).trim().toLowerCase()
    );
  // Checkboxes render a ✓ glyph; signatures render an image; others show text.
  const displayText = isCheck ? (checked ? "✓" : "") : isSignature ? "" : answer;

  // Three-state semantics: Needs review (amber) · Filled (green) · Empty (gray).
  const filled = isCheck ? checked : !!answer;
  const needsReview = !!field.needs_review;
  const stateBorder = needsReview ? "#F59E0B" : filled ? "#16A34A" : "#9CA3AF";
  const stateBg = needsReview
    ? "rgba(245, 158, 11, 0.12)"
    : filled
    ? "rgba(22, 163, 74, 0.08)"
    : "rgba(156, 163, 175, 0.10)";

  // Text answers: font scales to fill the box (with wrapping). Recomputed when
  // the answer or box size changes — so resizing the box rescales the text.
  const textFitPx = useMemo(
    () => (isCheck || isSignature ? 0 : fitFontPx(displayText, css.width - 8, css.height - 4)),
    [isCheck, isSignature, displayText, css.width, css.height]
  );

  const stateWord = needsReview ? "needs review" : filled ? "filled" : "empty";

  return (
    <div
      data-field-overlay="1"
      role={isViewMode ? undefined : "button"}
      tabIndex={isViewMode ? -1 : 0}
      aria-label={`${field.label || field.field_id} — ${stateWord}${
        answer ? `: ${answer}` : ""
      }`}
      title={
        isViewMode
          ? field.label || field.field_id
          : `${field.label || field.field_id}${
              answer ? `\n\nAnswer: ${answer}` : ""
            }\n\nClick or press Enter to edit · Drag to move · Hover for handles`
      }
      onMouseEnter={() => {
        if (isViewMode) return;
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        if (isViewMode) return;
        onMouseLeave?.();
      }}
      onMouseDown={(e) => {
        if (isViewMode || drawMode) return;
        onBboxMouseDown(e, field, "move", scale);
      }}
      onKeyDown={(e) => {
        if (isViewMode || drawMode) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate?.(field);
        }
      }}
      style={{
        position: "absolute",
        left: css.left,
        top: css.top,
        width: css.width,
        height: css.height,
        border: isViewMode
          ? "1.5px solid transparent"
          : `1.5px solid ${stateBorder}`,
        background: isViewMode ? "rgba(255, 255, 255, 0)" : stateBg,
        borderRadius: 2,
        boxShadow:
          !isViewMode && isHighlighted
            ? "0 0 0 4px rgba(201, 94, 190, 0.45), 0 0 16px rgba(201, 94, 190, 0.55)"
            : "none",
        transition:
          "border-color 240ms ease, background-color 240ms ease, box-shadow 200ms ease",
        pointerEvents: isViewMode || drawMode ? "none" : "auto",
        cursor: isViewMode ? "default" : drawMode ? "crosshair" : "move",
      }}
    >
      {!isFinalizedView && isSignature && answer && (
        <img
          src={answer}
          alt="signature"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      )}

      {showAnswer && !isFinalizedView && displayText && (
        isCheck ? (
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: Math.max(10, css.height * 0.85),
              fontWeight: 700,
              lineHeight: 1,
              color: "#000000",
              fontFamily: "Outfit, sans-serif",
              pointerEvents: "none",
            }}
          >
            {displayText}
          </span>
        ) : (
          // Text answers wrap and scale to fill the box.
          <span
            style={{
              position: "absolute",
              inset: 0,
              padding: "2px 4px",
              fontSize: textFitPx,
              lineHeight: 1.3,
              color: "#000000",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              overflow: "hidden",
              fontFamily: "Outfit, sans-serif",
              pointerEvents: "none",
              textDecoration: "none",
            }}
          >
            {displayText}
          </span>
        )
      )}

      {showAffordances && (
        <>
          <button
            type="button"
            title="Delete field"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRequestDelete(field);
            }}
            style={{
              position: "absolute",
              right: -10,
              top: -10,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#fff",
              border: "1.5px solid #B91C1C",
              color: "#B91C1C",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              pointerEvents: "auto",
              zIndex: 5,
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }}
          >
            <Trash2 size={11} />
          </button>

          {RESIZE_HANDLES.map(({ kind, style }) => (
            <div
              key={kind}
              onMouseDown={(e) =>
                onBboxMouseDown(e, field, `resize-${kind}`, scale)
              }
              style={{
                position: "absolute",
                width: HANDLE_SIZE,
                height: HANDLE_SIZE,
                background: "#fff",
                border: "1.5px solid #C95EBE",
                borderRadius: 2,
                pointerEvents: "auto",
                ...style,
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default BboxOverlay;
